import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as state from '../../../../../../state'

import * as chart from './chart'
import * as comparisonTable from './comparison-table'
import * as componentStore from './component-store'
import * as details from './details'
import * as metric from './metric'
import * as overflowMenu from './overflow-menu'
import * as reportCard from './report-card'
import * as scoreCard from './score-card'
import * as survey from './survey'
import * as table from './table'
import * as treeEditor from './tree-editor'
import * as treeTable from './tree-table'
import * as widgetFooter from './widget-footer'

/**
 * All {@link ents.core.mosaic.Widget.Type types} that are pre-wrapped
 * with {@link ds.layout.widget.Component} or do not require it.
 */
const EMBEDDED_WIDGET_TYPES = new Set([
  ents.core.mosaic.Widget.Type.Table,
  // Metric charts are displayed as flat cards and don't need widget component
  // wrapping
  ents.core.mosaic.Widget.Type.Metric,
  ents.core.mosaic.Widget.Type.TreeEditor,
  ents.core.mosaic.Widget.Type.TreeTable,
])

/** Shared Mosaic widget host used for page, modal, and slideout rendering contexts. */
@ngCore.Component({
  imports: [
    chart.Component,
    details.Component,
    ds.adaptiveCard.Component,
    ds.button.Component,
    ds.illustration.emptyState.Component,
    ds.layout.widget.Component,
    ds.loader.Component,
    metric.Component,
    overflowMenu.Component,
    reportCard.Component,
    scoreCard.Component,
    survey.Component,
    table.Component,
    treeEditor.Component,
    treeTable.Component,
    widgetFooter.Component,
    comparisonTable.Component,
  ],
  providers: [componentStore.Widget],
  selector: 'atn-mosaic-widget',
  standalone: true,
  styleUrl: './component.scss',
  templateUrl: './component.html',
})
export class Component<TType extends ents.core.mosaic.Widget.Type> {
  /** Exposing widget type enum @see {@link ents.core.mosaic.Widget.Type}  */
  public WidgetTypeEnum = ents.core.mosaic.Widget.Type
  /** @see {@link state.mosaic.endUser.currentRendering.Feature} */
  public readonly currentRendering = ngCore.inject(
    state.mosaic.endUser.currentRendering.Feature,
  )
  /** @see {@link state.variableOverrides.Feature} */
  public readonly variablesFeature = ngCore.inject(
    state.variableOverrides.Feature,
  )
  /** @see {@link componentStore.Widget} */
  public readonly widgetStore = ngCore.inject(componentStore.Widget<TType>)
  /** @see {@link componentStore.FieldCollectionStore} */

  /**
   * Widget's identifier, distinguishable from
   * {@link ents.core.mosaic.Widget.Rendering.id}. Multiple widgets can
   * share the same rendering id but represent different instances.
   *
   * __NOTE:__ If you don't know what to pass you can use
   * {@link state.mosaic.models.widget.Ref.Id.FOR_PREVIEW}
   */
  public readonly refIdSg =
    ngCore.input.required<state.mosaic.models.widget.Ref.Id>({ alias: 'refId' })

  /**
   * @see {@link ents.core.mosaic.Widget.Rendering}
   */
  public readonly renderingSg = ngCore.input<
    ents.core.mosaic.Widget.Rendering<TType> | undefined
  >(undefined, {
    alias: 'rendering',
  })

  /**
   *  Loading state of the widget
   *
   * @default false
   */
  public readonly loadingSg = ngCore.input<boolean>(false, {
    alias: 'loading',
  })
  /**
   * Error message to be displayed in the widget in case of an error
   *
   * @default undefined
   */
  public errorSg = ngCore.input<string | Error | undefined>(undefined, {
    alias: 'error',
  })
  /**
   * Indicates whether the widget is displayed on the page or inside an overlay.
   *
   * @default 'page'
   */
  public readonly renderModeSg = ngCore.input<'page' | 'modal' | 'slideout'>(
    'page',
    {
      alias: 'renderMode',
    },
  )
  /**
   * Returns true if widget has variable definitions
   */
  public hasVariablesSg = ngCore.computed(
    (widgetId = this.widgetStore.renderingIdSg()) =>
      widgetId &&
      this.variablesFeature.selectors
        .hasDefinitions({ widget: { widgetId } })
        .asSignal(),
  )
  /**
   * Returns true if widget has applied variable values
   */
  public hasAppliedVariableValuesSg = ngCore.computed(
    (
      widgetId = this.widgetStore.renderingIdSg(),
      refId = this.widgetStore.refIdSg(),
      context = this.currentRendering.selectors.context.asSignal(),
    ) =>
      !!(
        context &&
        widgetId &&
        this.variablesFeature.selectors
          .hasAppliedValues({
            widget: {
              appId: context.appId,
              pageId: context.pageId,
              refId,
              scenarioId: context.scenarioId,
              widgetId,
            },
          })
          .asSignal()
      ),
  )
  /** Indicates whether charts have legend */
  private readonly _hasChartLegendSg = ngCore.computed(
    (
      widgetType = this.widgetStore.typeSg(),
      content = this.widgetStore.contentSg(),
    ) => {
      if (widgetType !== this.WidgetTypeEnum.Charts) {
        // Obviously, only charts has a chart legend
        return false
      }

      const legendItems = chart.footer.Component.getLegendItemsFrom({
        content:
          content satisfies ents.core.mosaic.Widget.Content as ents.core.mosaic.Widget.Content<
            typeof widgetType
          >,
      })

      // Legend is not displayed if there are more than one item to display
      return legendItems.length > 1
    },
  )
  /** Indicates whether widget has footer */
  public hasFooterSg = ngCore.computed(
    (renderMode = this.renderModeSg()) =>
      renderMode !== 'slideout' &&
      (this._hasChartLegendSg() ||
        (renderMode === 'modal' &&
          this.widgetStore.fieldCollection.hasOnSaveActionSg())),
  )
  /**
   * Indicates if the widget type defined in {@link ents.core.mosaic.Widget.Type} is pre-wrapped
   * with {@link ds.layout.widget.Component}
   */
  public readonly isEmbeddedWidgetSg = ngCore.computed(
    (type = this.widgetStore.typeSg()) =>
      type && EMBEDDED_WIDGET_TYPES.has(type),
  )

  constructor() {
    /**
     *  Updates the {@link componentStore.Widget} rendering state whenever
     *  the rendering input changes.
     */
    ngCore.effect(() => {
      const rendering = this.renderingSg()
      this.widgetStore.patchState({ rendering })
    })
    /**
     * Updates the {@link componentStore.Widget} loading state whenever the
     * loading input changes.
     */
    ngCore.effect(() => {
      this.widgetStore.patchState({ isLoading: this.loadingSg() })
    })
    /**
     * Updates the {@link componentStore.Widget} error state whenever the
     * error input changes
     */
    ngCore.effect(() => {
      this.widgetStore.patchState({ error: this.errorSg() })
    })

    /**
     * Updates the {@link componentStore.Widget} refId state whenever the
     * {@link refIdSg} input changes
     */
    ngCore.effect(() => {
      this.widgetStore.patchState({ refId: this.refIdSg() })
    })
  }

  /**
   * Handles cancel button clicks.
   *
   * __NOTE:__ This button is only displayed when {@link renderModeSg} is set to
   * `modal`.
   */
  public cancelClickHandler(source: 'Footer' | 'Header'): void {
    // REVIEW: accessing currentRendering.modal directly in this component may
    // not be ideal. Alternative approach would be handling this in the modal
    // component, but that would require passing additional props and make
    // conditional handlers more complex
    this.currentRendering.subfeatures.modal.actions.setModal
      .from(`Mosaic.Modal.Widget.${source}.Buttons.Cancel.Clicked`)
      .dispatch({ widgetRef: undefined })
  }

  /**
   * Open variables slideout for current widget
   */
  public openVariablesSlideoutHandler(): void {
    const context = this.currentRendering.selectors.context.asSignal()
    const widgetId = this.widgetStore.renderingIdSg()
    const refId = this.widgetStore.refIdSg()
    if (context && widgetId && refId) {
      const { appId, pageId, scenarioId } = context
      this.variablesFeature.actions.setFocused
        .from('Mosaic.Widget.FiltersBtn.Clicked')
        .dispatch({
          widget: {
            appId,
            pageId,
            refId,
            scenarioId,
            widgetId,
          },
        })
    }
  }

  /**
   * Handles save icon clicks.
   *
   * __NOTE:__ This button is only displayed when {@link renderModeSg} is set to
   * `page`.
   */
  public saveIconClickHandler(): void {
    this.widgetStore.fieldCollection.executeOnSaveAction(
      'Mosaic.Modal.Widget.Header.Buttons.Save.Clicked',
    )
  }

  // TODO: This implementation is just for end user current rendering. Should be
  // discussed and implemented for use cases in widget dialog and page builder
  /** Handling Error state refresh button clicked */
  public refreshButtonClickHandler(): void {
    // Widget reference retrieved by corresponding refId
    const ref = this.currentRendering.subfeatures.widgets.selectors
      .getWidgetRef(this.refIdSg())
      .asSignal()

    const pageContext = this.currentRendering.selectors.context.asSignal()

    if (pageContext && ref) {
      // Dispatching render action
      this.currentRendering.subfeatures.widgets.actions.render
        .from(
          'Mosaic.EndUser.Rendering.Widget.ErrorState.Buttons.Refresh.Clicked',
        )
        .dispatch({
          pageContext,
          ref,
          // Variables should already be configured for this widget before refresh, so we only need to pass
          // ref variables
          variables: {
            ...ref.variables,
          },
        })
    }
  }
}
